import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Activity, Package } from "lucide-react";
import { format, eachDayOfInterval, eachWeekOfInterval, startOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface ProductionTrendsProps {
  dateRange: {
    start: Date;
    end: Date;
  };
}

export function ProductionTrends({ dateRange }: ProductionTrendsProps) {
  const { data: workOrders } = useQuery({
    queryKey: ["work-orders-trends", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select("*")
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString())
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: batches } = useQuery({
    queryKey: ["batches-trends", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_batches")
        .select("*")
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString())
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: inspections } = useQuery({
    queryKey: ["inspections-trends", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select("*")
        .gte("inspection_date", dateRange.start.toISOString())
        .lte("inspection_date", dateRange.end.toISOString())
        .order("inspection_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Determine if we should show daily or weekly data
  const daysDiff = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  );
  const useWeekly = daysDiff > 30;

  // Generate date intervals
  const intervals = useWeekly
    ? eachWeekOfInterval({ start: dateRange.start, end: dateRange.end }, { weekStartsOn: 1 })
    : eachDayOfInterval({ start: dateRange.start, end: dateRange.end });

  // Aggregate data by date
  const productionData = intervals.map((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const nextDate = useWeekly
      ? new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000)
      : new Date(date.getTime() + 24 * 60 * 60 * 1000);

    const dayOrders = workOrders?.filter((o) => {
      const orderDate = new Date(o.created_at);
      return orderDate >= date && orderDate < nextDate;
    }) || [];

    const dayBatches = batches?.filter((b) => {
      const batchDate = new Date(b.created_at);
      return batchDate >= date && batchDate < nextDate;
    }) || [];

    const dayInspections = inspections?.filter((i) => {
      const inspDate = new Date(i.inspection_date);
      return inspDate >= date && inspDate < nextDate;
    }) || [];

    return {
      date: useWeekly
        ? `Sem ${format(date, "w", { locale: es })}`
        : format(date, "dd/MM", { locale: es }),
      ordenes: dayOrders.length,
      ordenesCompletadas: dayOrders.filter((o) => o.status === "completed").length,
      lotes: dayBatches.length,
      cantidad: dayBatches.reduce((sum, b) => sum + (b.quantity || 0), 0),
      inspecciones: dayInspections.length,
      aprobadas: dayInspections.filter((i) => i.status === "passed").length,
      rechazadas: dayInspections.filter((i) => i.status === "failed").length,
    };
  });

  // Calculate trends
  const currentPeriodOrders = workOrders?.length || 0;
  const currentPeriodQuantity = batches?.reduce((sum, b) => sum + (b.quantity || 0), 0) || 0;

  // Simple trend calculation (comparing first half vs second half of period)
  const midPoint = Math.floor(productionData.length / 2);
  const firstHalf = productionData.slice(0, midPoint);
  const secondHalf = productionData.slice(midPoint);

  const firstHalfOrders = firstHalf.reduce((sum, d) => sum + d.ordenes, 0);
  const secondHalfOrders = secondHalf.reduce((sum, d) => sum + d.ordenes, 0);
  const ordersTrend = firstHalfOrders > 0
    ? ((secondHalfOrders - firstHalfOrders) / firstHalfOrders) * 100
    : 0;

  const firstHalfQuantity = firstHalf.reduce((sum, d) => sum + d.cantidad, 0);
  const secondHalfQuantity = secondHalf.reduce((sum, d) => sum + d.cantidad, 0);
  const quantityTrend = firstHalfQuantity > 0
    ? ((secondHalfQuantity - firstHalfQuantity) / firstHalfQuantity) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Trend Indicators */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tendencia de Ordenes</p>
                <p className="text-2xl font-bold">{currentPeriodOrders} ordenes</p>
              </div>
              <div
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded",
                  ordersTrend >= 0 ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"
                )}
              >
                {ordersTrend >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="font-medium">{Math.abs(ordersTrend).toFixed(1)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tendencia de Produccion</p>
                <p className="text-2xl font-bold">{currentPeriodQuantity.toLocaleString()} uds</p>
              </div>
              <div
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded",
                  quantityTrend >= 0 ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"
                )}
              >
                {quantityTrend >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                <span className="font-medium">{Math.abs(quantityTrend).toFixed(1)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Evolucion de Ordenes de Trabajo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={productionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="ordenes"
                  name="Total Ordenes"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.2}
                />
                <Area
                  type="monotone"
                  dataKey="ordenesCompletadas"
                  name="Completadas"
                  stroke="#22c55e"
                  fill="#22c55e"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Production Volume Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Volumen de Produccion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={productionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                <Bar dataKey="lotes" name="Lotes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cantidad" name="Unidades" fill="#06b6d4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Quality Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Tendencia de Calidad</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={productionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="inspecciones"
                  name="Inspecciones"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="aprobadas"
                  name="Aprobadas"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="rechazadas"
                  name="Rechazadas"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
